import React from 'react';
import {AppTitle} from '../../../Presentation/components/ui/Typography';
import SidebarLayout from '../../dashboard/components/SidebarLayout';
import ServiceProviderTable from '../components/ServiceProviderTable';
import AddServiceProviderForm from '../components/AddServiceProviderForm';
import UpdateServiceProviderForm from '../components/UpdateServiceProviderForm';
import ServiceProviderDetails from '../components/ServiceProviderDetails';
import {useServiceProvidersManagement} from '../hooks/useServiceProvidersManagement';
import Modal from '../../../Presentation/components/ui/Modal';
import Button from '../../../Presentation/components/ui/Button';
import {PermissionDeniedCard} from '../../../Presentation/components/common';
import {ServiceProvider} from '../models/serviceProvider.model';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';
import {useAuthorization} from '../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../constants/permissions';

export default function ServiceProvidersPage () {
  const {hasPermission} = useAuthorization();
  // Unified service providers management
  const {
    // UI State
    showAddForm, showUpdateForm, showDetailsModal,
    selectedProviderId, selectedProvider,

    // Data
    serviceProviders, pagination,

    // Loading States
    loading,

    // Error States
    error,

    // Actions
    handleAddSuccess, handleViewDetails, handleCloseDetails,
    handleEdit, handleUpdateSuccess, handleDelete,
    handlePageChange, handlePageSizeChange, refetch,

    // Modal Actions
    openAddForm, closeAddForm, openUpdateForm, closeUpdateForm,
    openDetailsModal, closeDetailsModal
  } = useServiceProvidersManagement();

  // Check if user has permission to view service providers
  if (!hasPermission(PERMISSIONS.READ_SERVICE_PROVIDERS) &&
      !hasPermission(PERMISSIONS.MANAGE_SERVICE_PROVIDERS)) {
    return (
      <SidebarLayout>
        <div className="p-6 min-h-screen rounded-xl bg-[#F2F3F5]">
          <PermissionDeniedCard message="ليس لديك صلاحيات للوصول إلى إدارة مزودي الخدمة. يرجى التواصل مع المدير للحصول على الصلاحيات المناسبة." />
        </div>
      </SidebarLayout>
    );
  }

  // All handlers are now provided by the unified hook

  const handleUpdateCancel = () => {
    closeUpdateForm();
  };

  const handleAddCancel = () => {
    closeAddForm();
  };

  // Check if user has permission to create service providers
  const canCreate = hasPermission(PERMISSIONS.CREATE_SERVICE_PROVIDERS) ||
                    hasPermission(PERMISSIONS.MANAGE_SERVICE_PROVIDERS);

  return (
    <SidebarLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <AppTitle>إدارة مزودي الخدمة</AppTitle>
          {canCreate && (
            <Button
              onClick={openAddForm}
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              }
            >
              إضافة مزود خدمة جديد
            </Button>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="mr-3">
                <h3 className="text-sm font-medium text-red-800">خطأ</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Service Providers Table */}
        <ServiceProviderTable
          data={serviceProviders}
          page={pagination?.page || 1}
          pageSize={pagination?.limit || PAGINATION_CONFIG.DEFAULT_PAGE_SIZE}
          totalCount={pagination?.total || 0}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onViewDetails={handleViewDetails}
          loading={loading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          showPageSizeSelector={true}
          showPaginationInfo={true}
          exportable={true}
        />

        {/* Add Form Modal */}
        <Modal
          open={showAddForm}
          onClose={handleAddCancel}
          title="إضافة مزود خدمة جديد"
          width="max-w-4xl"
        >
          <AddServiceProviderForm
            onSuccess={handleAddSuccess}
            onCancel={handleAddCancel}
          />
        </Modal>

        {/* Update Form Modal */}
        <Modal
          open={showUpdateForm}
          onClose={handleUpdateCancel}
          title="تحديث مزود الخدمة"
          width="max-w-4xl"
        >
          {selectedProvider && (
            <UpdateServiceProviderForm
              provider={selectedProvider}
              onSuccess={handleUpdateSuccess}
              onCancel={handleUpdateCancel}
            />
          )}
        </Modal>

        {/* Details Modal */}
        <ServiceProviderDetails
          providerId={selectedProviderId}
          isOpen={showDetailsModal}
          onClose={handleCloseDetails}
        />
      </div>
    </SidebarLayout>
  );
}
